import { useRef, useEffect, useState } from 'react'
import { motion, useInView, animate } from 'framer-motion'

const stats = [
  { value: 200000, suffix: '+', label: 'Professionals in network', note: 'Across tech hubs globally' },
  { value: 50000, suffix: '+', label: 'Technology specialists mapped', note: 'Engineering, Data, Product' },
  { value: 70000, suffix: '+', label: 'Data & AI professionals', note: 'From IC to Chief Data Officer' },
  { value: 10000, suffix: '+', label: 'Leadership roles catalogued', note: 'Director level and above' },
  { value: 50, suffix: '+', label: 'GCC & product mandates closed', note: 'In the last 36 months' },
]

function Counter({ value, suffix }) {
  const [display, setDisplay] = useState(value)
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, amount: 0.5 })
  const animated = useRef(false)

  useEffect(() => {
    if (!inView || animated.current) return
    animated.current = true
    const controls = animate(0, value, {
      duration: 2.2,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setDisplay(Math.round(v)),
    })
    return controls.stop
  }, [inView, value])

  return (
    <span ref={ref}>
      {display.toLocaleString()}{suffix}
    </span>
  )
}

export default function StatsSection() {
  return (
    <section
      data-testid="stats-section"
      className="py-32 md:py-40 px-6 md:px-12 lg:px-24 bg-[#0B1F3A] overflow-hidden"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mb-20"
        >
          <p className="text-[10px] tracking-[0.35em] uppercase text-white/30 mb-5 font-body font-semibold">
            Scale & Reach
          </p>
          <h2 className="text-[clamp(2.5rem,5vw,5rem)] font-heading font-black tracking-tighter leading-none text-white">
            Numbers that<br />
            <span className="font-light text-white/50">reflect the work</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-white/10">
          {stats.map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="py-12 md:py-16 border-b border-white/10 pr-8"
              data-testid={`stat-item-${i + 1}`}
            >
              <p className="text-[clamp(3rem,6vw,5.5rem)] font-heading font-black leading-none text-white tracking-tighter mb-3">
                <Counter value={stat.value} suffix={stat.suffix} />
              </p>
              <p className="text-sm font-body font-semibold text-white/70 mb-1">{stat.label}</p>
              <p className="text-xs font-body text-white/30">{stat.note}</p>
            </motion.div>
          ))}
        </div>
        <p className="mt-8 text-[9px] font-body text-white/20 tracking-wide">
          Figures are approximations reflecting network depth across platforms, proprietary databases, and direct referrals. Not independently verified.
        </p>
      </div>
    </section>
  )
}
